import { useState, useRef, useEffect, useCallback, forwardRef } from 'react'
import { Clock, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils/cn'

export interface TimePickerProps {
  value: string // 24h format "HH:MM" e.g. "07:00", "19:00"
  onChange: (value: string) => void
  label?: string
  error?: string
  className?: string
  disabled?: boolean
  name?: string
}

const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'))
const MINUTES = ['00', '15', '30', '45']
const PERIODS = ['AM', 'PM'] as const

function to12Hour(time24: string): { hour: string; minute: string; period: 'AM' | 'PM' } {
  const [h, m] = time24.split(':').map(Number)
  const period: 'AM' | 'PM' = h >= 12 ? 'PM' : 'AM'
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h
  return {
    hour: String(hour12).padStart(2, '0'),
    minute: String(m).padStart(2, '0'),
    period,
  }
}

function to24Hour(hour: string, minute: string, period: 'AM' | 'PM'): string {
  let h = parseInt(hour, 10)
  if (period === 'AM' && h === 12) h = 0
  else if (period === 'PM' && h !== 12) h += 12
  return `${String(h).padStart(2, '0')}:${minute}`
}

function formatDisplay(time24: string): string {
  if (!time24) return ''
  const { hour, minute, period } = to12Hour(time24)
  return `${hour}:${minute} ${period}`
}

// Snap a minute value to the nearest interval option
function snapMinute(m: string): string {
  const val = parseInt(m, 10)
  const options = [0, 15, 30, 45]
  let closest = options[0]
  let minDiff = Math.abs(val - options[0])
  for (const opt of options) {
    const diff = Math.abs(val - opt)
    if (diff < minDiff) {
      minDiff = diff
      closest = opt
    }
  }
  return String(closest).padStart(2, '0')
}

export const TimePicker = forwardRef<HTMLDivElement, TimePickerProps>(
  ({ value, onChange, label, error, className, disabled, name }, ref) => {
    const [isOpen, setIsOpen] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const hourColRef = useRef<HTMLDivElement>(null)
    const minuteColRef = useRef<HTMLDivElement>(null)
    const periodColRef = useRef<HTMLDivElement>(null)

    const parsed = to12Hour(value || '09:00')
    const [selectedHour, setSelectedHour] = useState(parsed.hour)
    const [selectedMinute, setSelectedMinute] = useState(snapMinute(parsed.minute))
    const [selectedPeriod, setSelectedPeriod] = useState<'AM' | 'PM'>(parsed.period)

    // Sync internal state when value prop changes
    useEffect(() => {
      if (value) {
        const p = to12Hour(value)
        setSelectedHour(p.hour)
        setSelectedMinute(snapMinute(p.minute))
        setSelectedPeriod(p.period)
      }
    }, [value])

    // Scroll selected items into view when dropdown opens
    useEffect(() => {
      if (isOpen) {
        const scrollToSelected = (colRef: React.RefObject<HTMLDivElement | null>, selectedValue: string) => {
          if (!colRef.current) return
          const selectedEl = colRef.current.querySelector(`[data-value="${selectedValue}"]`) as HTMLElement
          if (selectedEl) {
            selectedEl.scrollIntoView({ block: 'center', behavior: 'smooth' })
          }
        }
        // Small delay to ensure DOM is ready
        requestAnimationFrame(() => {
          scrollToSelected(hourColRef, selectedHour)
          scrollToSelected(minuteColRef, selectedMinute)
          scrollToSelected(periodColRef, selectedPeriod)
        })
      }
    }, [isOpen, selectedHour, selectedMinute, selectedPeriod])

    // Close on click outside
    useEffect(() => {
      if (!isOpen) return
      const handleClickOutside = (e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
          setIsOpen(false)
        }
      }
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen])

    const emitChange = useCallback(
      (h: string, m: string, p: 'AM' | 'PM') => {
        onChange(to24Hour(h, m, p))
      },
      [onChange]
    )

    const handleHourSelect = (h: string) => {
      setSelectedHour(h)
      emitChange(h, selectedMinute, selectedPeriod)
    }

    const handleMinuteSelect = (m: string) => {
      setSelectedMinute(m)
      emitChange(selectedHour, m, selectedPeriod)
    }

    const handlePeriodSelect = (p: 'AM' | 'PM') => {
      setSelectedPeriod(p)
      emitChange(selectedHour, selectedMinute, p)
    }

    const inputId = label?.toLowerCase().replace(/\s+/g, '-')

    return (
      <div className={cn('w-full', className)} ref={ref}>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
          >
            {label}
          </label>
        )}
        <div className="relative" ref={containerRef}>
          {/* Hidden input for form compatibility */}
          {name && <input type="hidden" name={name} value={value || ''} />}

          {/* Trigger input */}
          <button
            type="button"
            id={inputId}
            disabled={disabled}
            onClick={() => !disabled && setIsOpen(!isOpen)}
            className={cn(
              'w-full flex items-center justify-between px-4 py-3 rounded-lg border bg-white dark:bg-gray-900 text-left',
              'transition-colors duration-200',
              'hover:border-gray-300 dark:hover:border-gray-600',
              'focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none',
              error
                ? 'border-error-500 focus:border-error-500 focus:ring-error-500/20'
                : isOpen
                ? 'border-primary-500 ring-2 ring-primary-500/20'
                : 'border-gray-200 dark:border-gray-700',
              disabled && 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-800'
            )}
          >
            <span className={cn(
              'text-base',
              value ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'
            )}>
              {value ? formatDisplay(value) : 'Select time'}
            </span>
            <div className="flex items-center gap-2">
              {error ? (
                <AlertCircle className="h-5 w-5 text-error-500" />
              ) : (
                <Clock className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              )}
            </div>
          </button>

          {/* Dropdown */}
          {isOpen && !disabled && (
            <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-900 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="flex divide-x divide-gray-100 dark:divide-gray-800">
                {/* Hour column */}
                <div
                  ref={hourColRef}
                  className="flex-1 max-h-52 overflow-y-auto py-1 scroll-smooth"
                >
                  {HOURS.map((h) => (
                    <button
                      key={h}
                      type="button"
                      data-value={h}
                      onClick={() => handleHourSelect(h)}
                      className={cn(
                        'w-full py-2.5 px-3 text-center text-sm font-medium transition-colors',
                        'min-h-[44px] flex items-center justify-center',
                        selectedHour === h
                          ? 'bg-primary-500 text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/30'
                      )}
                    >
                      {h}
                    </button>
                  ))}
                </div>

                {/* Minute column */}
                <div
                  ref={minuteColRef}
                  className="flex-1 max-h-52 overflow-y-auto py-1 scroll-smooth"
                >
                  {MINUTES.map((m) => (
                    <button
                      key={m}
                      type="button"
                      data-value={m}
                      onClick={() => handleMinuteSelect(m)}
                      className={cn(
                        'w-full py-2.5 px-3 text-center text-sm font-medium transition-colors',
                        'min-h-[44px] flex items-center justify-center',
                        selectedMinute === m
                          ? 'bg-primary-500 text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/30'
                      )}
                    >
                      {m}
                    </button>
                  ))}
                </div>

                {/* AM/PM column */}
                <div
                  ref={periodColRef}
                  className="flex-1 max-h-52 overflow-y-auto py-1 scroll-smooth"
                >
                  {PERIODS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      data-value={p}
                      onClick={() => handlePeriodSelect(p)}
                      className={cn(
                        'w-full py-2.5 px-3 text-center text-sm font-medium transition-colors',
                        'min-h-[44px] flex items-center justify-center',
                        selectedPeriod === p
                          ? 'bg-primary-500 text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/30'
                      )}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        {error && (
          <p className="mt-1.5 text-sm text-error-500 flex items-center gap-1">
            <span>{error}</span>
          </p>
        )}
      </div>
    )
  }
)

TimePicker.displayName = 'TimePicker'
